/**
 * ErvaPOS - Main App Component
 */

import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { GlobalViewProvider, useGlobalView } from './context/GlobalViewContext';
import { Layout } from './components/Layout';

// Pages
import LoginPage from './pages/LoginPage';
import CustomerProductOrdersPage from './pages/CustomerProductOrdersPage';
import DashboardPage from './pages/DashboardPage';
import POSPage from './pages/POSPage';
import ProductsPage from './pages/ProductsPage';
import StockPage from './pages/StockPage';
import CustomersPage from './pages/CustomersPage';
import CustomerAccountPage from './pages/CustomerAccountPage';
import InventoryPage from './pages/InventoryPage';
import StockVariancesPage from './pages/StockVariancesPage';
import StockMovelPage from './pages/StockMovelPage';
import VouchersPage from './pages/VouchersPage';
import DayClosePage from './pages/DayClosePage';
import SuppliersPage from './pages/SuppliersPage';
import PurchaseInvoicesPage from './pages/PurchaseInvoicesPage';
import ReturnsPage from './pages/ReturnsPage';
import SettingsPage from './pages/SettingsPage';
import TimesheetsPage from './pages/TimesheetsPage';
import EmployeesPage from './pages/EmployeesPage';
import ExpensesPage from './pages/ExpensesPage';
import ReportsDashboardPage from './pages/ReportsDashboardPage';
import SupplierReplenishmentPage from './pages/SupplierReplenishmentPage';
import ProvidersPage from './pages/ProvidersPage';
import SAFTImportPage from './pages/SAFTImportPage';
import RotulosPage from './pages/RotulosPage';
import RotulosEditorPage from './pages/RotulosEditorPage';
import SalesHistoryPage from './pages/SalesHistoryPage';
import NotificationsBookingApp from './pages/notificationsbooking/NotificationsBookingApp';
import CommunicationsSMSPage from './pages/CommunicationsSMSPage';
import StockWriteoffsPage from './pages/StockWriteoffsPage';
import SupplierReturnsPage from './pages/SupplierReturnsPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    const requestedPath = `${location.pathname}${location.search || ''}`;
    try { window.sessionStorage.setItem('famapos_post_login_redirect', requestedPath); } catch (_) {}
    return <Navigate to={`/login?next=${encodeURIComponent(requestedPath)}`} replace state={{ from: requestedPath }} />;
  }

  return <Layout>{children}</Layout>;
};

  // Role Route (autorização por role)
  const RoleRoute = ({ allow = [], children }) => {
    const { user } = useAuth();
    const role = user?.role;

    if (!role) return <Navigate to="/login" replace />;

    if (allow.length > 0 && !allow.includes(role)) {
      return <Navigate to="/" replace />;
    }

    return children;
  };

  const GlobalRoute = ({ children }) => {
    const { isGlobalView } = useGlobalView();
    return isGlobalView ? children : <Navigate to="/" replace />;
  };


// Placeholder pages for routes we haven't fully implemented yet
const PlaceholderPage = ({ title, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center h-96 text-slate-500">
    {Icon && <Icon className="w-16 h-16 mb-4 opacity-50" />}
    <h2 className="text-xl font-semibold text-white mb-2">{title}</h2>
    <p>Esta página está em desenvolvimento</p>
  </div>
);

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
        <Route
        path="/login" 
        element={<LoginPage />}
      />

      {/* Protected Routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pos"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <POSPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <ProductsPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/stock"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <StockPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/stock-writeoffs"
        element={
          <ProtectedRoute>
            <RoleRoute allow={['staff', 'manager', 'admin']}>
              <StockWriteoffsPage />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-returns"
        element={
          <ProtectedRoute>
            <RoleRoute allow={['staff', 'manager', 'admin']}>
              <SupplierReturnsPage />
            </RoleRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <CustomersPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
        <Route
          path="/customer-account"
          element={
            <ProtectedRoute>
                <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                  <CustomerAccountPage />
                </RoleRoute>
              </ProtectedRoute>
          }
        />
        <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <SuppliersPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <PurchaseInvoicesPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/expenses"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <GlobalRoute><ExpensesPage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/customer-orders"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <CustomerProductOrdersPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <TimesheetsPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <GlobalRoute><EmployeesPage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <InventoryPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/stock-variances"
        element={
          <ProtectedRoute>
            <RoleRoute allow={['manager', 'admin']}>
              <GlobalRoute><StockVariancesPage /></GlobalRoute>
            </RoleRoute>
          </ProtectedRoute>
        }
      />

        <Route
          path="/stockmovel"
          element={
            <ProtectedRoute>
                <RoleRoute allow={['staff', 'manager', 'admin']}>
                  <StockMovelPage />
                </RoleRoute>
              </ProtectedRoute>
          }
        />
      <Route
        path="/vouchers"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <VouchersPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/returns"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['operator', 'staff', 'manager', 'admin']}>
                <ReturnsPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/supplier-replenishment"
        element={<ProtectedRoute><RoleRoute allow={['staff', 'manager', 'admin']}><SupplierReplenishmentPage /></RoleRoute></ProtectedRoute>}
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <GlobalRoute><ReportsDashboardPage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/day-close"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <GlobalRoute><DayClosePage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/saft-import"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <GlobalRoute><SAFTImportPage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/providers"
        element={
          <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <ProvidersPage />
              </RoleRoute>
            </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
                <RoleRoute allow={['admin']}>
                <GlobalRoute><SettingsPage /></GlobalRoute>
              </RoleRoute>
            </ProtectedRoute>
        }
      />

      {/* Catch all */}
        <Route
          path="/sales-history"
          element={
            <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <SalesHistoryPage />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        
        <Route
          path="/notificationsbooking"
          element={
            <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <NotificationsBookingApp />
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/rotulos"
          element={
            <ProtectedRoute>
              <RoleRoute allow={['staff', 'manager', 'admin']}>
                <RotulosPage />
              </RoleRoute>
            </ProtectedRoute>
          }
        />
        <Route path="/rotulos/novo" element={<ProtectedRoute><RoleRoute allow={['manager','admin']}><RotulosEditorPage /></RoleRoute></ProtectedRoute>} />
        <Route path="/rotulos/editar/:id" element={<ProtectedRoute><RoleRoute allow={['manager','admin']}><RotulosEditorPage /></RoleRoute></ProtectedRoute>} />

        <Route
          path="/communications-sms"
          element={
            <ProtectedRoute>
              <RoleRoute allow={['manager', 'admin']}>
                <CommunicationsSMSPage />
              </RoleRoute>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />

</Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <GlobalViewProvider>
          <AppRoutes />
          <Toaster 
          position="top-right"
          toastOptions={{
            style: {
              background: '#1e293b',
              border: '1px solid #334155',
              color: '#f8fafc',
            },
          }}
          />
        </GlobalViewProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
